import { IonButton, IonIcon } from '@ionic/react'
import { reloadSharp } from 'ionicons/icons'
import { useEffect, useRef } from 'react'
import SignaturePad from 'react-signature-pad-wrapper'
import { distToSegment } from '../../utils/utils'

interface Props {
  onChange: (object: {
    img: string | null | undefined
    percX: number
    percY: number
    allPoints?: boolean
    isLine?: boolean
  }) => void
}

const SignatureArea = ({ onChange }: Props) => {
  const canvasSize = { width: 100, height: 295 }

  const Signature = useRef<any>(null)
  const ParentCanvas = useRef<HTMLDivElement>(null)

  // Clear function for clear the canvas
  const clear = () => {
    Signature.current.clear()
    onChange({ img: null, percX: 0, percY: 0 })
  }

  const handleResize = () => {
    if (ParentCanvas.current && Signature.current) {
      const width = ParentCanvas.current.clientWidth
      const height = ParentCanvas.current.clientHeight

      Signature.current.signaturePad.canvas.width = width
      Signature.current.signaturePad.canvas.height = height
    }
    clear()
  }

  useEffect(() => {
    window.addEventListener('resize', handleResize)
    setTimeout(handleResize, 0)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  // workaround para llamar al callback en los cambios, ya que la libreria de firma
  // no contiene dicha funcionalidad.
  useEffect(() => {
    if (!Signature.current.signaturePad) return
    // Aca se puede poner "endStroke". Asi solo se calcula una vez cuando
    // El usuario termina de firmar. Pero tiene un bug que si levantas la firma
    // Fuera del canvas, no se llama el evento. Por lo que pueden quedar firmas incompletas.
    Signature.current.signaturePad.addEventListener(
      'endStroke',
      () => {
        const dPoints = Signature.current.toData()

        // Busco un "bounding box" para la firma
        // Para determinar el porcentaje que ocupa esta bounding box
        // Sobre los ejes X e Y
        const minPoint = { x: 0, y: 0 }
        const maxPoint = { x: 0, y: 0 }
        let allPoints = true
        dPoints.forEach(
          (pArr: {
            points: Array<{
              x: number
              y: number
            }>
          }) => {
            pArr.points.forEach((point, index) => {
              if (index > 1) allPoints = false
              minPoint.x = !minPoint.x || point.x < minPoint.x ? point.x : minPoint.x
              minPoint.y = !minPoint.y || point.y < minPoint.y ? point.y : minPoint.y
              maxPoint.x = !maxPoint.x || point.x > maxPoint.x ? point.x : maxPoint.x
              maxPoint.y = !maxPoint.y || point.y > maxPoint.y ? point.y : maxPoint.y
            })
          }
        )
        const percX = ((maxPoint.x - minPoint.x) / Signature.current.signaturePad.canvas.clientWidth) * 100.0
        const percY = ((maxPoint.y - minPoint.y) / Signature.current.signaturePad.canvas.clientHeight) * 100.0

        // Se verifica si es una linea sola, filtrando los puntos
        const noPoints = dPoints.filter((pArr: { points: Array<{ x: number; y: number }> }) => pArr.points.length > 1)
        let isLine = true
        const errMargin = 50

        // Si solo hay un segmento, se verifica si no es una linea
        if (noPoints.length === 1) {
          const firstPoint = noPoints[0].points[0]
          const lastPoint = noPoints[0].points[noPoints[0].points.length - 1]
          // Por cada punto, si la distancia entre el primer y ultimo es mayor a
          // cierto margen, es porque no es una linea
          noPoints[0].points.forEach((point: { x: number; y: number }) => {
            if (distToSegment(point, firstPoint, lastPoint) > errMargin) isLine = false
          })
        } else isLine = false

        onChange({
          img: Signature.current?.toDataURL(),
          percX,
          percY,
          allPoints,
          isLine
        })
      },
      { once: false }
    )
  }, [])

  return (
    <div className="signPad-area">
      <div className="signPad-containerCanvas">
        <div className="signPad-innerCanvas">
          <div ref={ParentCanvas} className="signPad-parentCanvas">
            <SignaturePad
              redrawOnResize
              ref={Signature}
              width={canvasSize.width}
              height={canvasSize.height}
              canvasProps={{ className: 'signPad-canvas' }}
            />
          </div>
        </div>
        <div>
          <hr className="signPad-hr" />
          <IonButton id="signPad-retry" color="none" className="signPad-retry" onClick={clear}>
            <IonIcon src={reloadSharp} />
            <span className="signPad-refresh">Reintentá</span>
          </IonButton>
        </div>
      </div>
    </div>
  )
}

export default SignatureArea
